import { compressImage } from "@/lib/image-compression";
import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import { dailyReportDatePrefill, madridCivilDate } from "@shared/shiftDuration";
import { dailyReportHoursWriteSchema } from "@shared/dailyReportHours";
import { ArrowLeft, Camera, X, FileSignature, AlertCircle } from "lucide-react";
import { useState, useRef } from "react";
import SignatureCanvas from "react-signature-canvas";
import { toast } from "sonner";
import { useLocation, useRoute } from "wouter";

interface PhotoWithPreview {
  file: File;
  preview: string;
  caption: string;
}

export default function NewDailyReport() {
  const [location, setLocation] = useLocation();
  const [, params] = useRoute("/installations/:id/daily-reports/new");
  const searchParams = new URLSearchParams(window.location.search);
  const fromShiftEnd = searchParams.get("from") === "shift_end";
  const rawShiftEndId = searchParams.get("shiftId");
  const parsedShiftEndId = rawShiftEndId === null ? undefined : Number(rawShiftEndId);
  const shiftEndId = Number.isInteger(parsedShiftEndId) && (parsedShiftEndId ?? 0) > 0 ? parsedShiftEndId : undefined;
  const invalidShiftEndId = rawShiftEndId !== null && shiftEndId === undefined;
  const preselectedInstallationId = params?.id || searchParams.get('installationId');
  const requestedReportDate = dailyReportDatePrefill(
    searchParams.get('date'),
    madridCivilDate(new Date()),
  );

  const { user } = useAuth();
  const signatureRef = useRef<SignatureCanvas>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const utils = trpc.useUtils();

  const [formData, setFormData] = useState({
    installationId: preselectedInstallationId || "",
    reportDate: requestedReportDate,
    workDescription: "",
    clientDescription: "",
    hoursWorked: "",
  });

  const [photos, setPhotos] = useState<PhotoWithPreview[]>([]);
  const [hasSignature, setHasSignature] = useState(false);
  const [showShiftEndOptions, setShowShiftEndOptions] = useState(false);
  const [pendingShiftEndReports, setPendingShiftEndReports] = useState<Array<{ date: string; installationId: number; installationName: string; totalMinutes: number | null }>>([]);
  const [shiftEndValidationUnavailable, setShiftEndValidationUnavailable] = useState(false);
  const [isRevalidatingShiftEnd, setIsRevalidatingShiftEnd] = useState(false);
  const canEndQuery = trpc.shifts.canEnd.useQuery(undefined, { enabled: false });
  const endShiftMutation = trpc.shifts.end.useMutation();

  const fetchShiftEndValidation = async () => {
    if (invalidShiftEndId) throw new Error("La jornada indicada no es válida.");
    return shiftEndId === undefined
      ? (await canEndQuery.refetch()).data
      : await utils.shifts.canEnd.fetch({ shiftId: shiftEndId });
  };

  const openNextPendingReport = (pendingReports: Array<{ date: string; installationId: number; installationName: string; totalMinutes: number | null }>) => {
    const next = pendingReports[0];
    if (!next) return false;
    setShowShiftEndOptions(false);
    setFormData({
      installationId: String(next.installationId),
      reportDate: next.date,
      workDescription: "",
      clientDescription: "",
      hoursWorked: "",
    });
    setPhotos([]);
    signatureRef.current?.clear();
    setHasSignature(false);
    const shiftParam = shiftEndId === undefined ? "" : `&shiftId=${shiftEndId}`;
    setLocation(`/daily-reports/new?installationId=${next.installationId}&date=${next.date}&from=shift_end${shiftParam}`);
    return true;
  };

  const revalidateAfterShiftEndReport = async () => {
    setIsRevalidatingShiftEnd(true);
    try {
      const refreshed = await fetchShiftEndValidation();
      if (refreshed?.date && refreshed.date !== formData.reportDate) {
        setShowShiftEndOptions(false);
        toast.error("El parte no corresponde a la fecha de la jornada; no se finalizará.");
        return;
      }
      setShiftEndValidationUnavailable(false);
      setPendingShiftEndReports(refreshed?.pendingReports ?? []);
      // Keep the remaining debt visible and let the technician either register
      // another shared part or explicitly end this shift with debt still open.
      setShowShiftEndOptions(true);
    } catch {
      setShiftEndValidationUnavailable(true);
      setPendingShiftEndReports([]);
      setShowShiftEndOptions(true);
      toast.error("Parte guardado, pero no se pudo revalidar ahora; el cierre volverá a comprobar la jornada.");
    } finally {
      setIsRevalidatingShiftEnd(false);
    }
  };

  const installationsQuery = trpc.installations.listForDailyReport.useQuery(
    { date: formData.reportDate },
    { enabled: Boolean(formData.reportDate) },
  );

  const createMutation = trpc.dailyReports.create.useMutation({
    onSuccess: async (data) => {
      if (data.alreadyExists) {
        toast.info(fromShiftEnd
          ? "Ya existía uno de tus partes para este día; revalidamos la cobertura."
          : "Ya existía tu parte para este día; abrimos ese registro.");
      } else toast.success("Parte diario creado exitosamente");
      if (fromShiftEnd) {
        await revalidateAfterShiftEndReport();
      } else {
        setLocation(`/daily-reports/${data.reportId}`);
      }
    },
    onError: (error) => {
      toast.error(error.message || "Error al crear el parte diario");
    },
  });

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    files.forEach((file) => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setPhotos((prev) => [
            ...prev,
            {
              file,
              preview: e.target?.result as string,
              caption: "",
            },
          ]);
        };
        reader.readAsDataURL(file);
      }
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removePhoto = (index: number) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const updatePhotoCaption = (index: number, caption: string) => {
    setPhotos((prev) =>
      prev.map((photo, i) => (i === index ? { ...photo, caption } : photo))
    );
  };

  const clearSignature = () => {
    signatureRef.current?.clear();
    setHasSignature(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.installationId) {
      toast.error("Selecciona una instalación");
      return;
    }

    if (fromShiftEnd) {
      try {
        const target = await fetchShiftEndValidation();
        if (!target?.date || target.date !== formData.reportDate) {
          toast.error("El parte debe tener la misma fecha que la jornada que se va a finalizar.");
          return;
        }
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "No se pudo validar la jornada.");
        return;
      }
    }

    if (!hasSignature) {
      toast.error("Debes firmar el parte diario");
      return;
    }

    const hoursWorkedResult = dailyReportHoursWriteSchema.safeParse(Number(formData.hoursWorked));
    if (!formData.hoursWorked.trim() || !hoursWorkedResult.success) {
      toast.error("Indica horas en incrementos de media hora.");
      return;
    }

    // Get signature as base64
    const signatureData = signatureRef.current?.toDataURL('image/png').split(',')[1];

    // Convert photos to base64 using compression
    const photosData = await Promise.all(
      photos.map(async (photo) => {
        try {
          // Compress to max 1200px, 0.8 quality
          const compressedBase64 = await compressImage(photo.file, 1200, 0.8);
          return {
            data: compressedBase64,
            caption: photo.caption || undefined,
          };
        } catch (e) {
          console.error("Compression failed", e);
          toast.error(`Error procesando imagen: ${photo.file.name}`);
          throw e;
        }
      })
    );

    createMutation.mutate({
      installationId: parseInt(formData.installationId),
      reportDate: new Date(formData.reportDate),
      workDescription: formData.workDescription,
      clientDescription: formData.clientDescription,
      hoursWorked: hoursWorkedResult.data,
      signatureData,
      photos: photosData,
    });
  };

  const finalizeShiftAfterReport = async () => {
    try {
      const validation = await fetchShiftEndValidation();
      if (!validation?.date || validation.date !== formData.reportDate) {
        toast.error("El parte no corresponde a la fecha de la jornada; no se finalizará.");
        return;
      }
      const result = await endShiftMutation.mutateAsync(shiftEndId === undefined ? undefined : { shiftId: shiftEndId });
      if (result.reportOutstanding) {
        toast.info("Jornada finalizada. Los partes siguen pendientes; el cierre no modifica ni completa la instalación.");
      } else {
        toast.success("Jornada finalizada");
      }
      setShowShiftEndOptions(false);
      setLocation("/dashboard");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No se pudo finalizar la jornada");
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => setLocation(fromShiftEnd ? "/dashboard" : "/daily-reports")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
        </div>

        <div>
          <h1 className="text-3xl font-bold text-foreground">Nuevo Parte Diario</h1>
          <p className="text-muted-foreground mt-2">
            Registra el trabajo realizado en la instalación
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Información General</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {installationsQuery.isSuccess && (!installationsQuery.data || installationsQuery.data.length === 0) && (
                <Alert variant="destructive" className="bg-amber-50 border-amber-200 text-amber-800">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <AlertTitle className="font-bold">Sin Asignación Diaria</AlertTitle>
                  <AlertDescription>
                    No tienes ninguna instalación habilitada para el {formData.reportDate} seleccionado.
                    Contacta con tu responsable si falta una asignación de ese día.
                  </AlertDescription>
                </Alert>
              )}
              <div className="grid gap-2">
                <Label htmlFor="installationId">Instalación *</Label>
                <Select
                  value={formData.installationId}
                  onValueChange={(value) =>
                    setFormData({ ...formData, installationId: value })
                  }
                  required
                >
                  <SelectTrigger className="w-full min-w-0">
                    <SelectValue className="min-w-0 flex-1 truncate" placeholder="Seleccionar instalación" />
                  </SelectTrigger>
                  <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-2rem)]">
                    {installationsQuery.data?.map((installation: { id: number; clientName: string; address: string }) => (
                      <SelectItem
                        key={installation.id}
                        value={installation.id.toString()}
                        className="whitespace-normal break-words"
                      >
                        {installation.clientName} - {installation.address}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="reportDate">Fecha *</Label>
                  <Input
                    id="reportDate"
                    type="date"
                    value={formData.reportDate}
                    onChange={(e) =>
                      setFormData({ ...formData, reportDate: e.target.value })
                    }
                    required
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="hoursWorked">Horas indicadas en el parte *</Label>
                  <Input
                    id="hoursWorked"
                    type="number"
                    min="0"
                    step="0.5"
                    value={formData.hoursWorked}
                    onChange={(e) =>
                      setFormData({ ...formData, hoursWorked: e.target.value })
                    }
                    required
                  />
                  <p className="text-xs text-muted-foreground">Dato informativo del parte; no representa horas individuales ni se usa para nómina.</p>
                </div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="workDescription">Detalle interno / incidencias (solo personal autorizado) *</Label>
                <Textarea
                  id="workDescription"
                  value={formData.workDescription}
                  onChange={(e) =>
                    setFormData({ ...formData, workDescription: e.target.value })
                  }
                  rows={5}
                  placeholder="Anota incidencias y detalle interno del trabajo..."
                  required
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="clientDescription">Descripción profesional para el cliente *</Label>
                <Textarea
                  id="clientDescription"
                  value={formData.clientDescription}
                  onChange={(e) =>
                    setFormData({ ...formData, clientDescription: e.target.value })
                  }
                  rows={5}
                  placeholder="Resume el trabajo realizado con un lenguaje claro y profesional..."
                  required
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Fotos de Avance</CardTitle>
              <CardDescription>
                Sube fotos del progreso de la obra
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoSelect}
                  className="hidden"
                  id="photo-upload"
                />
                <Button
                  type="button"
                  variant="outline"
                  asChild
                >
                  <label htmlFor="photo-upload" className="cursor-pointer flex items-center">
                    <Camera className="w-4 h-4 mr-2" />
                    Agregar Fotos
                  </label>
                </Button>
              </div>

              {photos.length > 0 && (
                <div className="grid gap-4 md:grid-cols-2">
                  {photos.map((photo, index) => (
                    <div key={index} className="relative border border-border rounded-lg p-4 space-y-2">
                      <div className="relative">
                        <img
                          src={photo.preview}
                          alt={`Foto ${index + 1}`}
                          className="w-full h-48 object-cover rounded-md"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          className="absolute top-2 right-2"
                          onClick={() => removePhoto(index)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      <Input
                        placeholder="Descripción de la foto (opcional)"
                        value={photo.caption}
                        onChange={(e) => updatePhotoCaption(index, e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Firma Digital</CardTitle>
              <CardDescription>
                Firma el parte diario para confirmar el trabajo realizado
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="border-2 border-dashed border-border rounded-lg p-4">
                <SignatureCanvas
                  ref={signatureRef}
                  canvasProps={{
                    className: "w-full h-48 border border-border rounded-md bg-background",
                  }}
                  onEnd={() => setHasSignature(true)}
                />
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={clearSignature}>
                  <X className="w-4 h-4 mr-2" />
                  Limpiar Firma
                </Button>
                {hasSignature && (
                  <div className="flex items-center text-sm text-green-600">
                    <FileSignature className="w-4 h-4 mr-2" />
                    Firma registrada
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-4 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => setLocation(fromShiftEnd ? "/dashboard" : "/daily-reports")}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? "Guardando..." : "Guardar Parte Diario"}
            </Button>
          </div>
        </form>

        <AlertDialog open={showShiftEndOptions} onOpenChange={setShowShiftEndOptions}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Parte guardado correctamente. ¿Qué quieres hacer?</AlertDialogTitle>
              <AlertDialogDescription>
                {pendingShiftEndReports.length > 0
                  ? `Quedan ${pendingShiftEndReports.length} ${pendingShiftEndReports.length === 1 ? "parte pendiente" : "partes pendientes"}. Puedes registrar otro antes de finalizar; si cierras ahora, la deuda seguirá abierta y la instalación no se completará.`
                  : shiftEndValidationUnavailable
                    ? "No se pudo revalidar ahora. El cierre volverá a comprobar la jornada; cualquier deuda documental seguirá abierta y la instalación no se completará."
                    : "La cobertura se ha revalidado. Puedes finalizar la jornada o continuar; continuar no reanudará el reloj automáticamente."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                disabled={isRevalidatingShiftEnd || endShiftMutation.isPending}
                onClick={() => setLocation("/dashboard")}
              >
                Continuar
              </AlertDialogCancel>
              {pendingShiftEndReports.length > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  disabled={isRevalidatingShiftEnd || endShiftMutation.isPending}
                  onClick={() => openNextPendingReport(pendingShiftEndReports)}
                >
                  Registrar siguiente parte
                </Button>
              )}
              <AlertDialogAction
                disabled={isRevalidatingShiftEnd || endShiftMutation.isPending}
                onClick={(event) => {
                  event.preventDefault();
                  void finalizeShiftAfterReport();
                }}
              >
                {endShiftMutation.isPending ? "Finalizando..." : "Finalizar jornada"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
